import axios from "axios";
import SweetAlert from "sweetalert2";
import { apiEndpoints } from "../config";

// Paramètres d'en-tête communs aux rapports Mourabaha (agence + utilisateur)
export interface EnteteAgenceMourabaha {
  codAgce2: string;
  libelle: string;
  banque: string;
  teleph: string;
  numReg: string;
  licence: string;
  numCon: string;
  bPoste: string;
  ville: string;
  nomUser: string;
  libperiodicite: string;
}

// Paramètres du rapport de simulation Mourabaha
export interface SimulationMourabahaRapportParams {
  CodeMourabaha: string;
  CoutAchatActif: number;
  MargeBenefice: number;
  MontantCharge:number,
  Nomcli?:string,
  nombreEch: number;
  periodicite: string;
  datePremEch: string | Date;
  MontantFrais?: number;
  numcli?: string;
}

// Paramètres du rapport tableau d'amortissement Mourabaha (demande, proposition, validation, règlement, remise)
export interface TabamountMourabahaRapportParams {
  frais?: number;
  NUMCLI: string;
  NOMCLI: string;
  NumeroDemande: string;
  CoutAchat: number;
  Marge: number;
  NombreEch: number;
  DatePremEch: string | Date;
}

const formatDateForApi = (d: string | Date): string =>
  d instanceof Date ? d.toISOString().split("T")[0] : d;

const showReportDialog = (blob: Blob, fileName: string) => {
  SweetAlert.fire({
    title: "Tableau d'amortissement généré",
    text: "Souhaitez-vous prévisualiser, imprimer ou télécharger le tableau d'amortissement ?",
    icon: "question",
    showCancelButton: true,
    showDenyButton: true,
    denyButtonText: "Imprimer",
    confirmButtonText: "Prévisualiser",
    cancelButtonText: "Télécharger",
  }).then((result) => {
    const file = new File([blob], fileName, { type: "application/pdf" });
    const fileUrl = URL.createObjectURL(file);

    if (result.isConfirmed) {
      // Prévisualiser
      window.open(fileUrl, "_blank");
    } else if (result.isDenied) {
      // Imprimer via iframe
      const printHtml = `
        <html>
          <head>
            <title>${fileName}</title>
            <style>
              html, body { margin: 0; height: 100%; }
              iframe { width: 100%; height: 100%; border: none; }
            </style>
          </head>
          <body>
            <iframe id="pdfFrame" src="${fileUrl}"></iframe>
            <script>
              const iframe = document.getElementById('pdfFrame');
              iframe.onload = function() {
                setTimeout(() => {
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                }, 500);
              };
            </script>
          </body>
        </html>
      `;
      const printBlob = new Blob([printHtml], { type: "text/html" });
      const printUrl = URL.createObjectURL(printBlob);
      window.open(printUrl, "_blank");
    } else {
      // Télécharger
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setTimeout(() => {
      URL.revokeObjectURL(fileUrl);
    }, 10000);
  });
};

const fetchAndShowReport = async (
  url: string,
  params: Record<string, any>,
  fileName: string,
) => {
  try {
    SweetAlert.fire({
      title: "Génération en cours",
      html: "Veuillez patienter pendant le chargement du tableau d'amortissement...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        SweetAlert.showLoading();
      },
    });

    const response = await axios.get(url, {
      params,
      responseType: "blob",
    });

    SweetAlert.close();

    if (response.data) {
      const blob = new Blob([response.data], { type: "application/pdf" });
      showReportDialog(blob, fileName);
    } else {
      SweetAlert.fire({
        title: "Erreur",
        text: "Erreur lors de la récupération du tableau d'amortissement.",
        icon: "error",
      });
    }
  } catch (error) {
    SweetAlert.close();
    console.error("Erreur lors de la génération du PDF :", error);
    SweetAlert.fire({ title: "Erreur", text: String(error), icon: "error" });
  }
};

// GET /api/RapportFinancement/simulation-mourabaha
export const PrintSimulationMourabaha = async (
  simulation: SimulationMourabahaRapportParams,
  entete: EnteteAgenceMourabaha,
  format: string = "PDF",
) => {
  const now = new Date();
  const stamp = `${now.toLocaleDateString("fr-FR").replace(/\//g, "-")}_${now.toLocaleTimeString("fr-FR").replace(/:/g, "-")}`;
  const fileName = `SimulationMourabaha_${simulation.CodeMourabaha}_${stamp}.${format.toLowerCase()}`;

  await fetchAndShowReport(
    `${apiEndpoints.rapportFinancement}/simulation-mourabaha`,
    {
      CodeMourabaha: simulation.CodeMourabaha,
      CoutAchatActif: simulation.CoutAchatActif,
      MargeBenefice: simulation.MargeBenefice,
      nombreEch: simulation.nombreEch,
      periodicite: simulation.periodicite,
      MontantCharge:simulation.MontantCharge,
      Nomcli: simulation.Nomcli ?? "",
      datePremEch: formatDateForApi(simulation.datePremEch),
      frais: simulation.MontantFrais ?? 0,
      numcli: simulation.numcli ?? "",
      ...entete,
      format,
    },
    fileName,
  );
};

// GET /api/RapportFinancement/TabamountMourabaha
export const PrintTabamountMourabaha = async (
  tab: TabamountMourabahaRapportParams,
  entete: EnteteAgenceMourabaha,
  format: string = "PDF",
) => {
  const now = new Date();
  const stamp = `${now.toLocaleDateString("fr-FR").replace(/\//g, "-")}_${now.toLocaleTimeString("fr-FR").replace(/:/g, "-")}`;
  const fileName = `TabAmortMourabaha_${tab.NumeroDemande}_${stamp}.${format.toLowerCase()}`;

  await fetchAndShowReport(
    `${apiEndpoints.rapportFinancement}/TabamountMourabaha`,
    {
      frais: tab.frais ?? 0,
      NUMCLI: tab.NUMCLI,
      NOMCLI: tab.NOMCLI,
      NumeroDemande: tab.NumeroDemande,
      CoutAchat: tab.CoutAchat,
      Marge: tab.Marge,
      NombreEch: tab.NombreEch,
      DatePremEch: formatDateForApi(tab.DatePremEch),
      ...entete,
      format,
    },
    fileName,
  );
};
